import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon, LayersIcon, LinkIcon, NetworkIcon } from '@app/components/icons';
import Drawer from '@app/components/Drawer';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import type {
  SiteIntelligenceDevice,
  SiteIntelligenceFindingPreview,
  SiteIntelligenceNamingFindingDetailResponse,
  SiteIntelligenceNamingListResponse,
  SiteIntelligenceNamingReviewQuestionListResponse,
  SiteIntelligenceReviewQuestionListResponse,
  SiteIntelligenceRoutingFindingDetailResponse,
  SiteIntelligenceRoutingFindingListResponse,
  SiteIntelligenceRoutingInstancePreview,
  SiteIntelligenceRoutingObservation,
  SiteIntelligenceRoutingOperatorEvidence,
  SiteIntelligenceRoutingReviewQuestionListResponse,
  SiteIntelligenceSummary,
  SiteIntelligenceTopologyFindingDetailResponse,
  SiteIntelligenceTopologyFindingListResponse,
  SiteIntelligenceTopologyReviewQuestionListResponse,
  SiteIntelligenceVlanFinding,
  SiteIntelligenceVlanDetailResponse,
  SiteIntelligenceVlanListResponse
} from '@app/types/sdk';
import { ResponseError, SitesApi } from '@app/types/sdk';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import './siteIntelligence.css';

type Collection = 'naming' | 'topology' | 'vlans' | 'routing' | 'naming-review-questions' | 'topology-review-questions' | 'routing-review-questions';

type EvidenceDrawer =
  | { kind: 'naming'; data: SiteIntelligenceNamingFindingDetailResponse }
  | { kind: 'topology'; data: SiteIntelligenceTopologyFindingDetailResponse; architectureBestFit?: string; summary?: SiteIntelligenceTopologyFindingDetailResponse; summaryUnavailable?: boolean }
  | { kind: 'routing'; data: SiteIntelligenceRoutingFindingDetailResponse }
  | { kind: 'vlan'; data: SiteIntelligenceVlanDetailResponse };

type CollectionError = { collection: Collection; message: string };

const architectureLabels: Record<string, string> = {
  two_tier: 'Two-tier',
  three_tier: 'Three-tier',
  collapsed_core: 'Collapsed core',
  spine_leaf: 'Spine–leaf',
  hub_and_spoke: 'Hub and spoke',
  firewall_centered_edge: 'Firewall-centered edge',
  mixed: 'Mixed',
  unclassified: 'Unclassified'
};

const architectureLabel = (value: string) => architectureLabels[value] ?? value;

const formatTimestamp = (value: Date) => value.toLocaleString(undefined, {
  timeZone: 'UTC',
  timeZoneName: 'short'
});

const SectionState = ({ state, failureCode }: { state: string; failureCode?: string | null }) => (
  <span className={`site-intelligence-state site-intelligence-state--${state}`}>
    {state === 'available' ? 'Observed' : state === 'partial' ? 'Partial' : state === 'notMaterialized' ? 'Not materialized' : 'Unavailable'}
    {failureCode ? ` · ${failureCode}` : ''}
  </span>
);

const Confidence = ({ value }: { value?: string | null }) => value ? (
  <span className={`site-intelligence-confidence site-intelligence-confidence--${value}`}>{value}</span>
) : null;

/** Explains why a named section has no usable result in the selected snapshot. */
const SectionUnavailableNotice = ({ section, state, failureCode }: { section: string; state: string; failureCode?: string | null }) => (
  <p className="site-intelligence-section-notice">
    {state === 'notMaterialized'
      ? `${section} was not materialized for this snapshot.`
      : `${section} is unavailable for this snapshot.`}
    {failureCode ? <span>Reason: {failureCode}</span> : null}
  </p>
);

const SiteIntelligencePage = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const orgId = useActiveOrgId();
  const apiConfiguration = useApiConfiguration();
  const sitesApi = useMemo(() => new SitesApi(apiConfiguration), [apiConfiguration]);
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SiteIntelligenceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCollection, setExpandedCollection] = useState<Collection | null>(null);
  const [scrollTarget, setScrollTarget] = useState<Collection | null>(null);
  const [naming, setNaming] = useState<SiteIntelligenceNamingListResponse | null>(null);
  const [namingReviewQuestions, setNamingReviewQuestions] = useState<SiteIntelligenceNamingReviewQuestionListResponse | null>(null);
  const [topology, setTopology] = useState<SiteIntelligenceTopologyFindingListResponse | null>(null);
  const [topologyReviewQuestions, setTopologyReviewQuestions] = useState<SiteIntelligenceTopologyReviewQuestionListResponse | null>(null);
  const [routing, setRouting] = useState<SiteIntelligenceRoutingFindingListResponse | null>(null);
  const [routingReviewQuestions, setRoutingReviewQuestions] = useState<SiteIntelligenceRoutingReviewQuestionListResponse | null>(null);
  const [vlans, setVlans] = useState<SiteIntelligenceVlanListResponse | null>(null);
  const [collectionLoading, setCollectionLoading] = useState<Collection | null>(null);
  const [collectionError, setCollectionError] = useState<CollectionError | null>(null);
  const [drawer, setDrawer] = useState<EvidenceDrawer | null>(null);
  const [drawerLoading, setDrawerLoading] = useState<string | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  // Every response is scoped to the route's organization and site. This
  // invalidates in-flight collections and evidence when either changes.
  const scopeVersionRef = useRef(0);
  // Closing the drawer or opening another finding invalidates its prior
  // request, so a late response cannot reopen evidence the user dismissed.
  const drawerRequestVersionRef = useRef(0);
  // A collection is mounted only after its action is selected. Keep one ref to
  // the active card so every collection action lands at the top of the module's
  // scrollable content area, below the persistent Site Intelligence heading.
  const collectionSectionRef = useRef<HTMLDivElement | null>(null);

  const loadSummary = useCallback(async (signal: AbortSignal, scopeVersion: number) => {
    const isCurrentScope = () => !signal.aborted && scopeVersionRef.current === scopeVersion;
    if (!orgId || !siteId) {
      if (!isCurrentScope()) return;
      setLoading(false);
      setError('Choose a site to view Site Intelligence.');
      return;
    }
    if (!isCurrentScope()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await sitesApi.getSiteIntelligenceSummary(
        { orgId, siteId },
        { signal }
      );
      if (!isCurrentScope()) return;
      setSummary(response);
    } catch (caught) {
      if (!isCurrentScope()) return;
      if (caught instanceof ResponseError && caught.response.status === 404) {
        setError('No completed Site Intelligence snapshot exists for this site yet.');
      } else if (!(caught instanceof Error && caught.name === 'AbortError')) {
        setError('Unable to load Site Intelligence.');
      }
      setSummary(null);
    } finally {
      if (isCurrentScope()) setLoading(false);
    }
  }, [orgId, siteId, sitesApi]);

  useLayoutEffect(() => {
    const scopeVersion = ++scopeVersionRef.current;
    drawerRequestVersionRef.current += 1;
    setSummary(null);
    setLoading(true);
    setError(null);
    setExpandedCollection(null);
    setScrollTarget(null);
    setNaming(null);
    setNamingReviewQuestions(null);
    setTopology(null);
    setTopologyReviewQuestions(null);
    setRouting(null);
    setRoutingReviewQuestions(null);
    setVlans(null);
    setCollectionLoading(null);
    setCollectionError(null);
    setDrawer(null);
    setDrawerLoading(null);
    setDrawerError(null);
    return () => {
      if (scopeVersionRef.current === scopeVersion) scopeVersionRef.current += 1;
    };
  }, [orgId, siteId]);

  useLayoutEffect(() => {
    if (!scrollTarget || expandedCollection !== scrollTarget) return;
    const section = collectionSectionRef.current;
    const module = section?.closest<HTMLElement>('.org-module');
    const scrollContainer = module?.querySelector<HTMLElement>('.org-module__scroll');

    if (section && scrollContainer) {
      // The module heading is outside this container. Align the newly opened
      // collection with the container's top edge so that heading remains in
      // view. Clamp the target to the available scroll range: near the end of
      // a short page, the browser shows as much of the collection as it can
      // instead of leaving blank space below it.
      const targetTop = scrollContainer.scrollTop
        + section.getBoundingClientRect().top
        - scrollContainer.getBoundingClientRect().top;
      const maximumScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      const top = Math.min(Math.max(0, targetTop), maximumScrollTop);
      if (typeof scrollContainer.scrollTo === 'function') {
        scrollContainer.scrollTo({
          behavior: 'smooth',
          top
        });
      } else {
        // Some embedded web views do not expose Element.scrollTo. The direct
        // assignment retains the same bounded destination without animation.
        scrollContainer.scrollTop = top;
      }
    } else {
      // The fallback keeps the interaction usable in a layout that does not
      // use OrgModuleLayout's internal scroll container.
      section?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }

    // Scroll once when the loading collection appears, then once more when its
    // final height is known. The second pass matters when the initial loading
    // state was too short to reach the intended position.
    if (collectionLoading !== scrollTarget) setScrollTarget(null);
  }, [collectionLoading, expandedCollection, scrollTarget]);

  useEffect(() => {
    const controller = new AbortController();
    void loadSummary(controller.signal, scopeVersionRef.current);
    return () => controller.abort();
  }, [loadSummary]);

  const loadCollection = async (collection: Collection, after?: string | null) => {
    const scopeVersion = scopeVersionRef.current;
    const isCurrentScope = () => scopeVersionRef.current === scopeVersion;
    if (!isCurrentScope()) return;
    setExpandedCollection(collection);
    setCollectionLoading(collection);
    setCollectionError(null);
    try {
      if (collection === 'naming') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceNamingFamilies({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setNaming((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'naming-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceNamingReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setNamingReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'topology') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceTopologyFindings({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setTopology((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'topology-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceTopologyReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setTopologyReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'routing') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceRoutingFindings({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setRouting((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'routing-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceRoutingReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setRoutingReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceVlans({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setVlans((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      }
    } catch (caught) {
      if (isCurrentScope() && !(caught instanceof Error && caught.name === 'AbortError')) {
        setCollectionError({ collection, message: 'Unable to load this collection. The other Site Intelligence sections are still available.' });
      }
    } finally {
      if (isCurrentScope()) {
        // A request for another collection may have started while this one was
        // in flight. Do not hide that newer collection's loading state.
        setCollectionLoading((current) => current === collection ? null : current);
      }
    }
  };

  const openCollection = (collection: Collection) => {
    setScrollTarget(collection);
    void loadCollection(collection);
  };

  const minimizeCollection = () => {
    setScrollTarget(null);
    setExpandedCollection(null);
  };

  const openFinding = async (kind: 'naming' | 'topology' | 'routing', finding: { findingId: string }) => {
    const scopeVersion = scopeVersionRef.current;
    const drawerRequestVersion = ++drawerRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && drawerRequestVersionRef.current === drawerRequestVersion;
    setDrawer(null);
    setDrawerError(null);
    setDrawerLoading('Loading observed evidence…');
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      if (kind === 'naming') {
        const response = await sitesApi.getSiteIntelligenceNamingFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setDrawer({ kind, data: response });
      } else if (kind === 'topology') {
        const response = await sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setDrawer({ kind, data: response });
      } else {
        const response = await sitesApi.getSiteIntelligenceRoutingFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setDrawer({ kind, data: response });
      }
    } catch {
      if (isCurrentRequest()) setDrawerError('Unable to load the evidence for this finding.');
    } finally {
      if (isCurrentRequest()) setDrawerLoading(null);
    }
  };

  // The architecture category and prose summary are separate public findings
  // with potentially different evidence. Load both into one drawer so each
  // statement remains grounded without making the operator open two panels.
  const openArchitectureEvidence = async (architecture: NonNullable<SiteIntelligenceSummary['sections']['topology']['architecture']>, topologySummary?: SiteIntelligenceFindingPreview) => {
    const scopeVersion = scopeVersionRef.current;
    const drawerRequestVersion = ++drawerRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && drawerRequestVersionRef.current === drawerRequestVersion;
    setDrawer(null);
    setDrawerError(null);
    setDrawerLoading('Loading observed evidence…');
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      const architectureRequest = sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: architecture.findingId, snapshotId: summary?.snapshot.id });
      const summaryRequest = topologySummary
        ? sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: topologySummary.findingId, snapshotId: summary?.snapshot.id }).catch(() => undefined)
        : Promise.resolve(undefined);
      const [architectureEvidence, summaryEvidence] = await Promise.all([architectureRequest, summaryRequest]);
      if (!isCurrentRequest()) return;
      setDrawer({ kind: 'topology', data: architectureEvidence, architectureBestFit: architecture.bestFit, summary: summaryEvidence, summaryUnavailable: Boolean(topologySummary && !summaryEvidence) });
    } catch {
      if (isCurrentRequest()) setDrawerError('Unable to load the evidence for this finding.');
    } finally {
      if (isCurrentRequest()) setDrawerLoading(null);
    }
  };

  const openVlan = async (vlanId: number) => {
    const scopeVersion = scopeVersionRef.current;
    const drawerRequestVersion = ++drawerRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && drawerRequestVersionRef.current === drawerRequestVersion;
    setDrawer(null);
    setDrawerError(null);
    setDrawerLoading(`Loading observed VLAN ${vlanId}…`);
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      const response = await sitesApi.getSiteIntelligenceVlan({ orgId, siteId, vlanId, snapshotId: summary?.snapshot.id });
      if (!isCurrentRequest()) return;
      setDrawer({ kind: 'vlan', data: response });
    } catch {
      if (isCurrentRequest()) setDrawerError(`Unable to load observed VLAN ${vlanId}.`);
    } finally {
      if (isCurrentRequest()) setDrawerLoading(null);
    }
  };

  const closeDrawer = () => {
    drawerRequestVersionRef.current += 1;
    setDrawer(null);
    setDrawerLoading(null);
    setDrawerError(null);
  };

  if (!orgId) {
    return <OrgModuleLayout title="Site Intelligence" description="Observed site facts and evidence-backed interpretations."><OrgModulePlaceholder intent="Select an organization to view a site." /></OrgModuleLayout>;
  }

  const topologyPreview = summary?.sections.topology;
  const observed = topologyPreview?.observed;
  const topologyAvailable = topologyPreview?.state === 'available';
  const namingAvailable = summary?.sections.naming.state === 'available';
  const routingPreview = summary?.sections.routing;
  const routingAvailable = routingPreview?.state === 'available';
  const vlansAvailable = summary?.sections.vlans.state === 'available';
  const topologyHasUnpreviewedQuestions = (topologyPreview?.reviewQuestionCount ?? 0) > (topologyPreview?.reviewQuestionPreview?.length ?? 0);
  const namingHasUnpreviewedQuestions = (summary?.sections.naming.reviewQuestionCount ?? 0) > (summary?.sections.naming.reviewQuestionPreview?.length ?? 0);
  return (
    <OrgModuleLayout
      title="Site Intelligence"
      description="Observed site facts and cautious interpretations, with evidence available on demand."
      actions={<button type="button" className="ui-button ui-button--ghost" onClick={() => navigate('/app/connectivity')}><ChevronLeftIcon size={16} />Back to sites</button>}
      scrollMode="all"
    >
      {loading ? <OrgModulePlaceholder intent="Loading Site Intelligence…" /> : null}
      {error ? <OrgModulePlaceholder intent={error} /> : null}
      {summary ? (
        <div className="site-intelligence-page">
          <header className="site-intelligence-page__header">
            <div>
              <p>Site Intelligence snapshot</p>
              <h2>Observed site summary</h2>
              <span>{summary.coverage.deviceCount} inventory {summary.coverage.deviceCount === 1 ? 'device' : 'devices'} selected</span>
            </div>
            <div className="site-intelligence-page__snapshot">
              <SectionState state={summary.snapshot.status} />
              <span>Generated {formatTimestamp(summary.snapshot.generatedAt)}</span>
            </div>
          </header>

          <section className="site-intelligence-overview" aria-label="Observed topology facts">
            <div className="site-intelligence-overview__heading">
              <div className="site-intelligence-overview__heading-main">
                <NetworkIcon size={20} />
                <div>
                  <p>Physical topology</p>
                  <div className="site-intelligence-overview__architecture">
                    <h3>{topologyPreview?.architecture ? `${architectureLabel(topologyPreview.architecture.bestFit)} architecture` : 'No architecture interpretation'}</h3>
                    <Confidence value={topologyPreview?.architecture?.confidence} />
                  </div>
                </div>
              </div>
              <SectionState state={topologyPreview?.state ?? 'notMaterialized'} failureCode={topologyPreview?.failureCode} />
            </div>
            {topologyPreview?.summary ? <p className="site-intelligence-overview__summary">{topologyPreview.summary.text}</p> : <p className="site-intelligence-overview__summary">No topology interpretation is available for this snapshot.</p>}
            {observed ? <dl className="site-intelligence-stat-grid">
              <div><dt>Matched devices</dt><dd>{observed.matchedDeviceCount} / {observed.inventoryDeviceCount}</dd></div>
              <div><dt>Physical adjacencies</dt><dd>{observed.physicalAdjacencyCount}</dd></div>
              <div><dt>Physical connections</dt><dd>{observed.physicalConnectionCount}</dd></div>
              <div><dt>HA pairs</dt><dd>{observed.haPairCount}</dd></div>
              <div><dt>Components</dt><dd>{observed.componentCount}</dd></div>
              <div><dt>Isolated devices</dt><dd>{observed.isolatedDeviceCount}</dd></div>
            </dl> : null}
            {topologyAvailable ? <><div className="site-intelligence-overview__actions">
              {topologyPreview?.architecture ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openArchitectureEvidence(topologyPreview.architecture!, topologyPreview.summary)}>View architecture evidence</button> : null}
              <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('topology')}>View topology findings{topologyPreview?.findingCount ? ` (${topologyPreview.findingCount})` : ''}</button>
              {topologyHasUnpreviewedQuestions ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('topology-review-questions')}>View all topology review questions ({topologyPreview!.reviewQuestionCount})</button> : null}
            </div>
            {topologyPreview?.reviewQuestionPreview?.length ? <FindingPreviewList title="Topology review questions" findings={topologyPreview.reviewQuestionPreview} onOpen={(finding) => void openFinding('topology', finding)} /> : null}</> : <SectionUnavailableNotice section="Physical topology" state={topologyPreview?.state ?? 'notMaterialized'} failureCode={topologyPreview?.failureCode} />}
          </section>

          <div className="site-intelligence-section-grid">
            <section className="site-intelligence-section-card">
              <header><div><LayersIcon size={20} /><div><p>Hostname naming</p><h3>{summary.sections.naming.familyCount ?? 0} observed {summary.sections.naming.familyCount === 1 ? 'family' : 'families'}</h3></div></div><SectionState state={summary.sections.naming.state} failureCode={summary.sections.naming.failureCode} /></header>
              {namingAvailable ? <><p>{summary.sections.naming.summary?.text ?? 'No naming interpretation is available for this snapshot.'}</p>
              <dl className="site-intelligence-inline-stats"><div><dt>Variants</dt><dd>{summary.sections.naming.variantCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{summary.sections.naming.reviewQuestionCount ?? 0}</dd></div></dl>
              <div className="site-intelligence-overview__actions">
                {summary.sections.naming.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openFinding('naming', summary.sections.naming.summary!)}>View naming summary evidence</button> : null}
                <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('naming')}>View naming families</button>
                {namingHasUnpreviewedQuestions ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('naming-review-questions')}>View all naming review questions ({summary.sections.naming.reviewQuestionCount})</button> : null}
              </div>
              {summary.sections.naming.reviewQuestionPreview?.length ? <FindingPreviewList title="Review questions" findings={summary.sections.naming.reviewQuestionPreview} onOpen={(finding) => void openFinding('naming', finding)} /> : null}</> : <SectionUnavailableNotice section="Hostname naming" state={summary.sections.naming.state} failureCode={summary.sections.naming.failureCode} />}
            </section>

            <section className="site-intelligence-section-card">
              <header><div><LinkIcon size={20} /><div><p>Observed VLAN catalog</p><h3>{summary.sections.vlans.vlanCount ?? 0} VLANs</h3></div></div><SectionState state={summary.sections.vlans.state} failureCode={summary.sections.vlans.failureCode} /></header>
              {vlansAvailable ? <><p>{summary.sections.vlans.summary?.text ?? 'No VLAN catalog is available for this snapshot.'}</p>
              <dl className="site-intelligence-inline-stats"><div><dt>Member devices</dt><dd>{summary.sections.vlans.memberDeviceCount ?? 0}</dd></div><div><dt>Member interfaces</dt><dd>{summary.sections.vlans.memberInterfaceCount ?? 0}</dd></div></dl>
              <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('vlans')}>View VLAN catalog</button></> : <SectionUnavailableNotice section="Observed VLAN catalog" state={summary.sections.vlans.state} failureCode={summary.sections.vlans.failureCode} />}
            </section>

            <section className="site-intelligence-section-card site-intelligence-routing-card">
              <header><div><NetworkIcon size={20} /><div><p>BGP routing adjacency</p><h3>{routingPreview?.resolvedAdjacencyCount ?? 0} resolved in-site links</h3></div></div><SectionState state={routingPreview?.state ?? 'notMaterialized'} failureCode={routingPreview?.failureCode} /></header>
              {routingAvailable ? <><section className="site-intelligence-routing-overview">
                <div><span>Observed overview</span>{routingPreview.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openFinding('routing', routingPreview.summary!)}>Evidence</button> : null}</div>
                <p>{routingPreview.summary?.text ?? 'No BGP overview is available for this snapshot.'}</p>
              </section>
              <dl className="site-intelligence-inline-stats"><div><dt>BGP instances</dt><dd>{routingPreview.instanceCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{routingPreview.reviewQuestionCount ?? 0}</dd></div></dl>
              <div className="site-intelligence-routing-actions">
                <button type="button" className="ui-button ui-button--primary" onClick={() => openCollection('routing')}>Explore BGP findings</button>
                {(routingPreview.reviewQuestionCount ?? 0) > 0 ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('routing-review-questions')}>Review questions</button> : null}
              </div>
              {routingPreview.instancePreview?.length ? <RoutingInstancePreviewList instances={routingPreview.instancePreview} onOpen={(finding) => void openFinding('routing', finding)} /> : null}
              {routingPreview.commonalityPreview?.length ? <FindingPreviewList title="Common observed patterns" findings={routingPreview.commonalityPreview} onOpen={(finding) => void openFinding('routing', finding)} /> : null}
              {routingPreview.observedDifferencePreview?.length ? <FindingPreviewList title="Observed differences and attention" findings={routingPreview.observedDifferencePreview} onOpen={(finding) => void openFinding('routing', finding)} /> : null}
              </> : <SectionUnavailableNotice section="BGP routing adjacency" state={routingPreview?.state ?? 'notMaterialized'} failureCode={routingPreview?.failureCode} />}
            </section>
          </div>

          {expandedCollection === 'topology' ? <div ref={collectionSectionRef}><TopologyCollection list={topology} loading={collectionLoading === 'topology'} error={collectionError?.collection === 'topology' ? collectionError.message : null} onOpen={(finding) => void openFinding('topology', finding)} onMore={() => topology?.meta.next && void loadCollection('topology', topology.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'topology-review-questions' ? <div ref={collectionSectionRef}><ReviewQuestionCollection title="Topology review questions" list={topologyReviewQuestions} loading={collectionLoading === 'topology-review-questions'} error={collectionError?.collection === 'topology-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('topology', finding)} onMore={() => topologyReviewQuestions?.meta.next && void loadCollection('topology-review-questions', topologyReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'naming' ? <div ref={collectionSectionRef}><NamingCollection list={naming} loading={collectionLoading === 'naming'} error={collectionError?.collection === 'naming' ? collectionError.message : null} onOpen={(finding) => void openFinding('naming', finding)} onMore={() => naming?.meta.next && void loadCollection('naming', naming.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'naming-review-questions' ? <div ref={collectionSectionRef}><ReviewQuestionCollection title="Naming review questions" list={namingReviewQuestions} loading={collectionLoading === 'naming-review-questions'} error={collectionError?.collection === 'naming-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('naming', finding)} onMore={() => namingReviewQuestions?.meta.next && void loadCollection('naming-review-questions', namingReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'routing' ? <div ref={collectionSectionRef}><RoutingCollection list={routing} loading={collectionLoading === 'routing'} error={collectionError?.collection === 'routing' ? collectionError.message : null} onOpen={(finding) => void openFinding('routing', finding)} onMore={() => routing?.meta.next && void loadCollection('routing', routing.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'routing-review-questions' ? <div ref={collectionSectionRef}><RoutingReviewQuestionCollection list={routingReviewQuestions} loading={collectionLoading === 'routing-review-questions'} error={collectionError?.collection === 'routing-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('routing', finding)} onMore={() => routingReviewQuestions?.meta.next && void loadCollection('routing-review-questions', routingReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'vlans' ? <div ref={collectionSectionRef}><VlanCollection list={vlans} loading={collectionLoading === 'vlans'} error={collectionError?.collection === 'vlans' ? collectionError.message : null} onOpen={(vlanId) => void openVlan(vlanId)} onMore={() => vlans?.meta.next && void loadCollection('vlans', vlans.meta.next)} onMinimize={minimizeCollection} /></div> : null}
        </div>
      ) : null}

      <EvidenceDrawer drawer={drawer} loading={drawerLoading} error={drawerError} onClose={closeDrawer} />
    </OrgModuleLayout>
  );
};

const CollectionShell = ({ title, description, loading, error, children, onMore, hasMore, onMinimize }: { title: string; description: string; loading: boolean; error?: string | null; children: ReactNode; onMore: () => void; hasMore?: boolean; onMinimize: () => void }) => (
  <section className="site-intelligence-collection">
    <header><div><p>{title}</p><h3>{description}</h3></div><button type="button" className="ui-button ui-button--ghost site-intelligence-collection__minimize" onClick={onMinimize}>Minimize</button></header>
    {loading ? <p className="site-intelligence-loading">Loading…</p> : children}
    {error ? <p className="site-intelligence-error">{error}</p> : null}
    {hasMore ? <button type="button" className="ui-button ui-button--ghost" onClick={onMore}>Load more</button> : null}
  </section>
);

const TopologyCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceTopologyFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Topology findings" description={`${list?.findingCount ?? 0} evidence-backed observations`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <FindingList findings={list?.items ?? []} onOpen={onOpen} /> : <EmptyCollection message="No topology findings were generated for this snapshot." />}
  </CollectionShell>
);

const RoutingCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => {
  // The landing card already presents the one site-level overview. This
  // collection intentionally starts with details that add operator value.
  const findings = (list?.items ?? []).filter((finding) => finding.kind !== 'summary');
  return <CollectionShell title="BGP instance and design findings" description="Per-instance observations and cautious design context" loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (findings.length ? <RoutingFindingList findings={findings} onOpen={onOpen} /> : <EmptyCollection message="No additional BGP findings were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="BGP routing adjacency" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No BGP findings were loaded for this snapshot." />}
  </CollectionShell>;
};

const RoutingReviewQuestionCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Routing review questions" description={`${list?.reviewQuestionCount ?? 0} questions for this snapshot`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message="No routing review questions were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="Routing review questions" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No routing review questions were loaded for this snapshot." />}
  </CollectionShell>
);

const NamingCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceNamingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Naming families" description={`${list?.familyCount ?? 0} observed hostname families`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <ul className="site-intelligence-row-list">
      {(list?.items ?? []).map((family) => <li key={family.findingId}><div><strong>{family.pattern}</strong><span>{family.interpretation}</span><small>{family.supportCount} devices · {family.variantCount} variants</small></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen({ findingId: family.findingId, text: family.interpretation, confidence: family.confidence })}>Evidence</button></li>)}
    </ul> : <EmptyCollection message="No hostname naming families were generated for this snapshot." />}
  </CollectionShell>
);

const ReviewQuestionCollection = ({ title, list, loading, error, onOpen, onMore, onMinimize }: { title: string; list: SiteIntelligenceReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title={title} description={`${list?.reviewQuestionCount ?? 0} questions for this snapshot`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message="No review questions were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section={title} state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No review questions were loaded for this snapshot." />}
  </CollectionShell>
);

const VlanCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceVlanListResponse | null; loading: boolean; error?: string | null; onOpen: (vlanId: number) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Observed VLAN catalog" description={`${list?.vlanCount ?? 0} VLANs in this snapshot`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <div className="site-intelligence-table-wrap">
      <table className="site-intelligence-table">
        <thead><tr><th scope="col">VLAN</th><th scope="col">Observed names</th><th scope="col">Members</th><th scope="col">Name status</th><th scope="col">Purpose preview</th><th scope="col"><span className="sr-only">Open detail</span></th></tr></thead>
        <tbody>
          {(list?.items ?? []).map((vlan) => <tr key={vlan.vlanId}>
            <th scope="row"><button type="button" className="site-intelligence-vlan-link" onClick={() => onOpen(vlan.vlanId)}>VLAN {vlan.vlanId}</button></th>
            <td>{deviceIds(vlan.observedNames).length ? deviceIds(vlan.observedNames).join(', ') : 'No observed name'}</td>
            <td>{vlan.memberDeviceCount} {vlan.memberDeviceCount === 1 ? 'device' : 'devices'}</td>
            <td><span className={`site-intelligence-name-status site-intelligence-name-status--${vlan.nameStatus}`}>{vlan.nameStatus}</span></td>
            <td>{vlan.purposePreview?.text ?? 'No purpose interpretation'}</td>
            <td><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(vlan.vlanId)}>Evidence</button></td>
          </tr>)}
        </tbody>
      </table>
    </div> : <EmptyCollection message="No observed VLANs were generated for this snapshot." />}
  </CollectionShell>
);

const EmptyCollection = ({ message }: { message: string }) => <p className="site-intelligence-empty-state">{message}</p>;

type FindingReference = { findingId: string; text: string; confidence?: string | null };

const FindingList = ({ findings, onOpen }: { findings: FindingReference[]; onOpen: (finding: FindingReference) => void }) => (
  <ul className="site-intelligence-row-list">
    {findings.map((finding) => <li key={finding.findingId}><div><strong>{finding.text}</strong><Confidence value={finding.confidence} /></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}
  </ul>
);

const routingFindingKindLabel = (kind: string) => ({
  summary: 'Routing overview',
  instance_summary: 'Instance summary',
  interpretation: 'Design hypothesis',
  commonality: 'Observed commonality',
  observed_difference: 'Observed difference'
}[kind] ?? kind.replaceAll('_', ' '));

const RoutingFindingList = ({ findings, onOpen }: { findings: SiteIntelligenceRoutingFindingListResponse['items']; onOpen: (finding: FindingReference) => void }) => (
  <ul className="site-intelligence-row-list">
    {findings.map((finding) => <li key={finding.findingId}><div><small>{routingFindingKindLabel(finding.kind)}{finding.instance ? ` · ${finding.instance}` : ''}</small><strong>{finding.text}</strong><Confidence value={finding.confidence} /></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}
  </ul>
);

const FindingPreviewList = ({ title, findings, onOpen }: { title: string; findings: FindingReference[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-preview-list">
    <h4>{title}</h4>
    <ul>{findings.map((finding) => <li key={finding.findingId}><span>{finding.text}</span><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}</ul>
  </section>
);

/** Keeps the landing page concise; the complete instance narrative is loaded only when evidence is opened. */
const RoutingInstancePreviewList = ({ instances, onOpen }: { instances: SiteIntelligenceRoutingInstancePreview[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-routing-instances">
    <h4>BGP instances</h4>
    <div className="site-intelligence-routing-table-wrap">
      <table>
        <thead><tr><th scope="col">Instance</th><th scope="col">Visible devices</th><th scope="col">Resolved in-site links</th><th scope="col">Evidence</th></tr></thead>
        <tbody>{instances.map((instance) => <tr key={instance.instance}>
          <th scope="row"><code>{instance.instance}</code></th>
          <td>{instance.visibleDeviceCount}</td>
          <td>{instance.resolvedAdjacencyCount}</td>
          <td>{instance.summary ? <button type="button" className="ui-button ui-button--ghost" aria-label={`Open evidence for ${instance.instance}`} onClick={() => onOpen(instance.summary!)}>Open</button> : null}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>
);

const EvidenceDrawer = ({ drawer, loading, error, onClose }: { drawer: EvidenceDrawer | null; loading: string | null; error: string | null; onClose: () => void }) => {
  const title = drawer?.kind === 'vlan' ? `VLAN ${drawer.data.vlan.vlanId}` : drawer?.kind === 'naming' ? 'Naming evidence' : drawer?.kind === 'routing' ? 'BGP routing evidence' : 'Topology evidence';
  return (
    <Drawer open={Boolean(drawer || loading || error)} title={title} subtitle={drawer?.kind === 'vlan' ? 'Observed membership and current name evidence' : 'Public, typed evidence for this statement'} onClose={onClose} width="wide">
      {loading ? <p className="site-intelligence-loading">{loading}</p> : null}
      {error ? <p className="site-intelligence-error">{error}</p> : null}
      {drawer?.kind === 'topology' ? <TopologyEvidence data={drawer.data} architectureBestFit={drawer.architectureBestFit} summary={drawer.summary} summaryUnavailable={drawer.summaryUnavailable} /> : null}
      {drawer?.kind === 'naming' ? <NamingEvidence data={drawer.data} /> : null}
      {drawer?.kind === 'routing' ? <RoutingEvidence data={drawer.data} /> : null}
      {drawer?.kind === 'vlan' ? <VlanEvidence data={drawer.data} /> : null}
    </Drawer>
  );
};

const deviceFor = (devices: SiteIntelligenceDevice[], deviceId: string) => devices.find((device) => device.id === deviceId);
/** Normalizes generated unique collections and JSON-array responses for rendering. */
const deviceIds = (values: Iterable<string>) => [...values];
const uniqueStrings = (values: Iterable<string>) => [...new Set(values)];
const uniqueBy = <T,>(values: Iterable<T>, key: (value: T) => string): T[] => {
  const unique = new Map<string, T>();
  for (const value of values) unique.set(key(value), value);
  return [...unique.values()];
};

/** Renders a public device reference without exposing its UUID as page text. */
const DeviceLink = ({ devices, deviceId }: { devices: SiteIntelligenceDevice[]; deviceId: string }) => {
  const device = deviceFor(devices, deviceId);
  const { siteId } = useParams<{ siteId: string }>();
  const orgId = useActiveOrgId();
  if (!device?.hostname) return <>Unknown device</>;
  const params = new URLSearchParams({ deviceId: device.id });
  if (siteId) params.set('siteId', siteId);
  if (orgId) params.set('orgId', orgId);
  return <Link className="site-intelligence-device-link" title={`Open ${device.hostname} device details`} to={`/app/inventory?${params.toString()}`}>{device.hostname}</Link>;
};

const DeviceLinks = ({ devices, deviceIds: ids }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string> }) => <>
  {deviceIds(ids).map((deviceId, index, allDeviceIds) => <span key={deviceId}><DeviceLink devices={devices} deviceId={deviceId} />{index < allDeviceIds.length - 1 ? ', ' : null}</span>)}
</>;

/** Displays each observed device as a compact hostname and inventory-role chip. */
const DeviceChipList = ({ devices, deviceIds: ids, showRole = true }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string>; showRole?: boolean }) => (
  <ul className="site-intelligence-device-chip-list">
    {deviceIds(ids).map((deviceId) => {
      const device = deviceFor(devices, deviceId);
      return <li key={deviceId}><DeviceLink devices={devices} deviceId={deviceId} />{showRole && device?.storedRole ? <span>{device.storedRole}</span> : null}</li>;
    })}
  </ul>
);

const EvidenceGroup = ({ title, children }: { title: string; children: ReactNode }) => <section className="site-intelligence-evidence-group"><h3>{title}</h3>{children}</section>;

type TopologySubjectRoleGroup = {
  role: string;
  deviceIds: string[];
};

/** Groups exact finding subjects by their inventory-backed role for quick topology scanning. */
const topologySubjectRoleGroups = (devices: SiteIntelligenceDevice[], deviceIds: Iterable<string>): TopologySubjectRoleGroup[] => {
  const groups = new Map<string, string[]>();
  for (const deviceId of deviceIds) {
    const role = deviceFor(devices, deviceId)?.storedRole?.trim() || 'No stored role';
    const group = groups.get(role) ?? [];
    group.push(deviceId);
    groups.set(role, group);
  }
  return [...groups.entries()]
    .map(([role, groupedDeviceIDs]) => ({ role, deviceIds: groupedDeviceIDs }))
    .sort((left, right) => {
      if (left.role === 'No stored role') return 1;
      if (right.role === 'No stored role') return -1;
      return left.role.localeCompare(right.role);
    });
};

const TopologySubjectTable = ({ devices, deviceIds, caption }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string>; caption: string }) => {
  const groups = topologySubjectRoleGroups(devices, deviceIds);
  return <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-subject-table">
    <caption className="sr-only">{caption}</caption>
    <thead><tr><th scope="col">Stored role</th><th scope="col">Observed devices</th><th scope="col">Count</th></tr></thead>
    <tbody>{groups.map((group) => <tr key={group.role}><th scope="row">{group.role}</th><td><DeviceChipList devices={devices} deviceIds={group.deviceIds} showRole={false} /></td><td>{group.deviceIds.length}</td></tr>)}</tbody>
  </table></div>;
};

const TopologyEvidence = ({ data, architectureBestFit, summary, summaryUnavailable }: { data: SiteIntelligenceTopologyFindingDetailResponse; architectureBestFit?: string; summary?: SiteIntelligenceTopologyFindingDetailResponse; summaryUnavailable?: boolean }) => {
  const finding = data.finding;
  // A topology summary can cite observations outside the architecture decision
  // (for example isolated firewalls). Its prose belongs in this drawer, but its
  // evidence must not be presented as an architectural layer.
  const devices = data.devices;
  const physicalLinks = uniqueBy(data.finding.evidence.physicalLinks, (link) => [...[link.fromDeviceId, link.toDeviceId].sort(), String(link.linkCount)].join(':'));
  const haPairs = uniqueBy(data.finding.evidence.haPairs, (pair) => deviceIds(pair.deviceIds).sort().join(':'));
  const components = uniqueBy(data.finding.evidence.components, (component) => deviceIds(component.deviceIds).sort().join(':'));
  const statementTitle = architectureBestFit ? 'Architecture classification' : 'Topology finding';
  const statementText = architectureBestFit ? `${architectureLabel(architectureBestFit)} architecture` : finding.text;
  const subjectDeviceIds = uniqueStrings(deviceIds(finding.subjectDeviceIds));
  const architectureLayerDeviceIds = architectureBestFit
    ? uniqueStrings([...subjectDeviceIds, ...physicalLinks.flatMap((link) => [link.fromDeviceId, link.toDeviceId])])
    : subjectDeviceIds;
  const singleSubjectID = subjectDeviceIds.length === 1 ? subjectDeviceIds[0] : null;
  const hasDistinctComponentEvidence = components.some((component) => deviceIds(component.deviceIds).some((deviceID) => !subjectDeviceIds.includes(deviceID)));
  const showComponents = components.length > 0 && (physicalLinks.length > 0 || haPairs.length > 0 || hasDistinctComponentEvidence);
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>{statementTitle}</span>
      <p>{statementText}</p>
      <Confidence value={finding.confidence} />
      {singleSubjectID ? <div className="site-intelligence-interpretation-card__subject"><span>Applies to</span><DeviceLink devices={devices} deviceId={singleSubjectID} /></div> : null}
    </section>
    {summary ? <section className="site-intelligence-interpretation-card">
      <span>Topology summary</span>
      <p>{summary.finding.text}</p>
    </section> : null}
    {summaryUnavailable ? <p className="site-intelligence-section-notice">Topology summary evidence is unavailable; architecture evidence remains available below.</p> : null}
    <dl className="site-intelligence-evidence-stats">
      <div><dt>{architectureBestFit ? 'Architecture devices' : 'Subjects'}</dt><dd>{architectureLayerDeviceIds.length}</dd></div>
      <div><dt>Physical links</dt><dd>{physicalLinks.length}</dd></div>
      <div><dt>HA pairs</dt><dd>{haPairs.length}</dd></div>
      <div><dt>Components</dt><dd>{components.length}</dd></div>
    </dl>
    {architectureLayerDeviceIds.length ? <EvidenceGroup title={architectureBestFit ? 'Architecture layers observed' : 'Observed subjects'}><TopologySubjectTable devices={devices} deviceIds={architectureLayerDeviceIds} caption={architectureBestFit ? 'Devices participating in the architecture evidence, grouped by stored role' : 'Devices that are the subject of this topology finding, grouped by stored role'} /></EvidenceGroup> : null}
    {physicalLinks.length ? <EvidenceGroup title="Observed physical connections"><div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
      <caption className="sr-only">Physical device-to-device connections supporting this topology finding</caption>
      <thead><tr><th scope="col">Device A</th><th scope="col">Relationship</th><th scope="col">Device B</th><th scope="col">Connections</th></tr></thead>
      <tbody>{physicalLinks.map((link) => <tr key={`${link.fromDeviceId}-${link.toDeviceId}`}><td><DeviceLink devices={devices} deviceId={link.fromDeviceId} /></td><td className="site-intelligence-relationship-symbol" aria-label="directly connected to">↔</td><td><DeviceLink devices={devices} deviceId={link.toDeviceId} /></td><td>{link.linkCount}</td></tr>)}</tbody>
    </table></div></EvidenceGroup> : null}
    {haPairs.length ? <EvidenceGroup title="Observed HA relationships"><div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
      <caption className="sr-only">High-availability device pairs supporting this topology finding</caption>
      <thead><tr><th scope="col">Device A</th><th scope="col">Relationship</th><th scope="col">Device B</th></tr></thead>
      <tbody>{haPairs.map((pair) => {
        const pairDeviceIds = deviceIds(pair.deviceIds);
        return <tr key={pairDeviceIds.join('-')}><td><DeviceLink devices={devices} deviceId={pairDeviceIds[0]} /></td><td className="site-intelligence-relationship-symbol" aria-label="high availability pair with">⇄</td><td><DeviceLink devices={devices} deviceId={pairDeviceIds[1]} /></td></tr>;
      })}</tbody>
    </table></div></EvidenceGroup> : null}
    {showComponents ? <EvidenceGroup title="Observed connected components"><div className="site-intelligence-component-list">{components.map((component, index) => {
      const componentDeviceIds = deviceIds(component.deviceIds);
      return <details key={componentDeviceIds.join('-')}><summary><strong>Component {index + 1}</strong><span>{componentDeviceIds.length} {componentDeviceIds.length === 1 ? 'device' : 'devices'}</span></summary><DeviceChipList devices={devices} deviceIds={component.deviceIds} /></details>;
    })}</div></EvidenceGroup> : null}
  </div>;
};

const routingObservationLabels: Record<string, string> = {
  resolved_bgp_fabric: 'Resolved BGP fabric',
  session_type_pattern: 'BGP session type pattern',
  address_family_pattern: 'BGP address-family pattern',
  paired_bgp_asn_relationship_pattern: 'Local and remote ASN pairing pattern',
  bgp_hold_time_rollup_coverage_incomplete: 'Hold-timer rollup coverage incomplete',
  bgp_negotiated_version_rollup_coverage_incomplete: 'Negotiated-version rollup coverage incomplete',
  instance_resolved_adjacency_coverage: 'Instance resolved-adjacency coverage',
  instance_session_type_pattern: 'Instance BGP session type pattern',
  instance_bgp_hold_time_rollup_coverage_incomplete: 'Instance hold-timer rollup coverage incomplete',
  instance_without_resolved_adjacency: 'Instance without resolved in-site adjacency',
  shared_stored_role: 'Shared stored role',
  instance_uniform_bgp_negotiated_version_rollup: 'Uniform negotiated BGP version rollup',
  repeated_dual_homing: 'Repeated dual-homing pattern'
};

const routingObservationLabel = (kind: string) => routingObservationLabels[kind] ?? kind.replaceAll('_', ' ');

const routingValueLabel = (value: string) => ({
  ebgp: 'eBGP',
  ibgp: 'iBGP',
  ipv4_unicast: 'IPv4 unicast',
  ipv6_unicast: 'IPv6 unicast',
  distinct_local_remote_as: 'distinct local and remote ASNs'
}[value] ?? value);

const routingObservationScope = (observation: SiteIntelligenceRoutingObservation): string => {
  const scope: string[] = [];
  if (observation.instance) scope.push(observation.instance);
  if (observation.role) scope.push(`Stored role: ${observation.role}`);
  if (observation.metric) scope.push(observation.metric.replaceAll('_', ' '));
  return scope.join(' · ') || 'Site-wide';
};

/**
 * Formats observation counts using their source contract. Instance coverage uses
 * observedCount for visible device nodes; all other observations use it for the
 * population that supplied the stated routing fact.
 */
const routingObservationCoverage = (observation: SiteIntelligenceRoutingObservation): string => {
  const counts: Array<[string, number | undefined]> = [
    ['Eligible', observation.eligibleCount],
    [
      observation.kind === 'instance_resolved_adjacency_coverage' || observation.kind === 'instance_without_resolved_adjacency'
        ? 'Visible devices'
        : 'Observed',
      observation.observedCount
    ],
    ['Matching', observation.matchingCount],
    ['Exceptions', observation.exceptionCount]
  ];
  return counts.filter(([, value]) => value !== undefined).map(([label, value]) => `${label}: ${value}`).join(' · ') || '—';
};

/** Formats the public scope fields that accompany one operator-facing BGP fact. */
const routingOperatorEvidenceScope = (evidence: SiteIntelligenceRoutingOperatorEvidence): string => {
  const scope: string[] = [];
  if (evidence.instance) scope.push(evidence.instance);
  if (evidence.role) scope.push(`Stored role: ${evidence.role}`);
  if (evidence.metric) scope.push(evidence.metric.replaceAll('_', ' '));
  return scope.join(' · ') || 'Site-wide';
};

const routingOperatorEvidenceStatusLabel = (status: SiteIntelligenceRoutingOperatorEvidence['status']): string => ({
  complete: 'Complete',
  incomplete: 'Incomplete coverage',
  variation: 'Variation',
  attention: 'Attention',
  informational: 'Informational'
}[status]);

const routingFindingTitle = (finding: SiteIntelligenceRoutingFindingDetailResponse['finding']) => {
  if (finding.kind === 'review_question') return 'Review question';
  return finding.category === 'design_hypothesis' ? 'Cautious design hypothesis' : routingFindingKindLabel(finding.kind);
};

const RoutingEvidence = ({ data }: { data: SiteIntelligenceRoutingFindingDetailResponse }) => {
  const observations = data.finding.evidence.observations;
  const operatorEvidence = data.finding.evidence.operatorEvidence ?? [];
  const evidenceFacts = operatorEvidence.length ? operatorEvidence : observations;
  const observedDeviceIds = uniqueStrings(evidenceFacts.flatMap((fact) => deviceIds(fact.deviceIds)));
  const peerDeviceIds = uniqueStrings(evidenceFacts.flatMap((fact) => deviceIds(fact.peerDeviceIds)));
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>{routingFindingTitle(data.finding)}</span>
      <p>{data.finding.text}</p>
      <Confidence value={data.finding.confidence} />
    </section>
    <dl className="site-intelligence-evidence-stats">
      <div><dt>BGP facts</dt><dd>{evidenceFacts.length}</dd></div>
      <div><dt>Devices in evidence</dt><dd>{observedDeviceIds.length}</dd></div>
      <div><dt>Peer devices</dt><dd>{peerDeviceIds.length}</dd></div>
    </dl>
    <EvidenceGroup title="BGP facts">
      {operatorEvidence.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table">
        <caption className="sr-only">Operator-facing BGP facts supporting this finding</caption>
        <thead><tr><th scope="col">BGP fact</th><th scope="col">Instance or scope</th><th scope="col">Values</th><th scope="col">Measures</th><th scope="col">Status</th></tr></thead>
        <tbody>{operatorEvidence.map((evidence, index) => {
          const values = deviceIds(evidence.values);
          return <tr key={`${evidence.type}:${evidence.instance ?? 'site'}:${index}`}>
            <th scope="row">{evidence.title}</th>
            <td>{routingOperatorEvidenceScope(evidence)}</td>
            <td>{values.length ? values.map(routingValueLabel).join(', ') : '—'}</td>
            <td>{evidence.measures.length ? evidence.measures.map((measure) => `${measure.label}: ${measure.value}`).join(' · ') : '—'}</td>
            <td>{routingOperatorEvidenceStatusLabel(evidence.status)}</td>
          </tr>;
        })}</tbody>
      </table></div> : observations.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table">
        <caption className="sr-only">Deterministic BGP observations supporting this finding</caption>
        <thead><tr><th scope="col">Observation</th><th scope="col">Instance or scope</th><th scope="col">Values</th><th scope="col">Coverage</th><th scope="col">Resolved in-site links</th><th scope="col">Confidence</th></tr></thead>
        <tbody>{observations.map((observation, index) => {
          const values = deviceIds(observation.values);
          return <tr key={`${observation.kind}:${observation.instance ?? 'site'}:${index}`}>
            <th scope="row">{routingObservationLabel(observation.kind)}</th>
            <td>{routingObservationScope(observation)}</td>
            <td>{values.length ? values.map(routingValueLabel).join(', ') : '—'}</td>
            <td>{routingObservationCoverage(observation)}</td>
            <td>{observation.adjacencyCount ?? '—'}</td>
            <td><Confidence value={observation.maxConfidence} /></td>
          </tr>;
        })}</tbody>
      </table></div> : <p>No deterministic observations were supplied for this finding.</p>}
    </EvidenceGroup>
    {observedDeviceIds.length ? <EvidenceGroup title="Observed devices"><DeviceChipList devices={data.devices} deviceIds={observedDeviceIds} /></EvidenceGroup> : null}
    {peerDeviceIds.length ? <EvidenceGroup title="Observed peer devices"><DeviceChipList devices={data.devices} deviceIds={peerDeviceIds} /></EvidenceGroup> : null}
  </div>;
};

type NamingEvidenceRow = {
  deviceId: string;
  familyPattern?: string;
  variantPattern?: string;
  explainedBy: string[];
};

type NamingVariantInterpretation = {
  familyPattern: string;
  variantPattern: string;
  interpretation: string;
};

/** Converts nested naming-family evidence into one readable row per supporting device. */
const namingEvidenceRows = (data: SiteIntelligenceNamingFindingDetailResponse): NamingEvidenceRow[] => {
  const rows = new Map<string, NamingEvidenceRow>();
  const representedDeviceIds = new Set<string>();
  for (const family of data.finding.evidence.families) {
    for (const deviceId of family.deviceIds) {
      const variant = family.variants.find((candidate) => deviceIds(candidate.deviceIds).includes(deviceId));
      rows.set(`${family.pattern}:${deviceId}`, {
        deviceId,
        familyPattern: family.pattern,
        variantPattern: variant?.pattern,
        explainedBy: variant ? deviceIds(variant.explainedBy) : []
      });
      representedDeviceIds.add(deviceId);
    }
  }
  for (const deviceId of data.finding.evidence.supportingDeviceIds) {
    if (!representedDeviceIds.has(deviceId)) rows.set(`supporting:${deviceId}`, { deviceId, explainedBy: [] });
  }
  return [...rows.values()].sort((left, right) => {
    const leftName = deviceFor(data.devices, left.deviceId)?.hostname ?? left.deviceId;
    const rightName = deviceFor(data.devices, right.deviceId)?.hostname ?? right.deviceId;
    return leftName.localeCompare(rightName);
  });
};

/** Returns each supplied variant interpretation once, independent of device count. */
const namingVariantInterpretations = (data: SiteIntelligenceNamingFindingDetailResponse): NamingVariantInterpretation[] => {
  const interpretations = new Map<string, NamingVariantInterpretation>();
  for (const family of data.finding.evidence.families) {
    for (const variant of family.variants) {
      interpretations.set(`${family.pattern}:${variant.pattern}`, {
        familyPattern: family.pattern,
        variantPattern: variant.pattern,
        interpretation: variant.interpretation
      });
    }
  }
  return [...interpretations.values()].sort((left, right) => left.variantPattern.localeCompare(right.variantPattern));
};

const NamingEvidence = ({ data }: { data: SiteIntelligenceNamingFindingDetailResponse }) => {
  const rows = namingEvidenceRows(data);
  const interpretations = namingVariantInterpretations(data);
  return <div className="site-intelligence-evidence">
    <p className="site-intelligence-evidence__statement">{data.finding.text}</p>
    <Confidence value={data.finding.confidence} />
    {interpretations.length ? <EvidenceGroup title="Naming pattern interpretations"><ul className="site-intelligence-interpretation-list">{interpretations.map((interpretation) => <li key={`${interpretation.familyPattern}:${interpretation.variantPattern}`}><strong>{interpretation.variantPattern}</strong><p>{interpretation.interpretation}</p></li>)}</ul></EvidenceGroup> : null}
    <EvidenceGroup title="Supporting devices">
      {rows.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Devices supporting this hostname naming finding</caption>
        <thead><tr><th scope="col">Device</th><th scope="col">Stored role</th><th scope="col">Hostname family</th><th scope="col">Observed variant</th><th scope="col">Explained by</th></tr></thead>
        <tbody>{rows.map((row) => {
          const device = deviceFor(data.devices, row.deviceId);
          return <tr key={`${row.familyPattern ?? 'supporting'}:${row.deviceId}`}><td><DeviceLink devices={data.devices} deviceId={row.deviceId} /></td><td>{device?.storedRole ?? '—'}</td><td>{row.familyPattern ?? '—'}</td><td>{row.variantPattern ?? '—'}</td><td>{row.explainedBy.length ? row.explainedBy.join(', ') : '—'}</td></tr>;
        })}</tbody>
      </table></div> : <p>No supporting device evidence was supplied.</p>}
    </EvidenceGroup>
  </div>;
};

type VlanMemberDeviceGroup = {
  deviceId: string;
  interfaceNames: string[];
};

/** Groups individual interface memberships under their reporting device without losing interface evidence. */
const vlanMemberDeviceGroups = (data: SiteIntelligenceVlanDetailResponse): VlanMemberDeviceGroup[] => {
  const grouped = new Map<string, Set<string>>();
  for (const member of data.vlan.members) {
    const interfaces = grouped.get(member.deviceId) ?? new Set<string>();
    interfaces.add(member.interfaceName);
    grouped.set(member.deviceId, interfaces);
  }
  return [...grouped.entries()]
    .map(([deviceId, interfaceNames]) => ({ deviceId, interfaceNames: [...interfaceNames].sort((left, right) => left.localeCompare(right)) }))
    .sort((left, right) => (deviceFor(data.devices, left.deviceId)?.hostname ?? left.deviceId).localeCompare(deviceFor(data.devices, right.deviceId)?.hostname ?? right.deviceId));
};

const InterfaceChipList = ({ interfaceNames }: { interfaceNames: string[] }) => <ul className="site-intelligence-interface-chip-list">
  {interfaceNames.map((interfaceName) => <li key={interfaceName}>{interfaceName}</li>)}
</ul>;

const VlanFindingList = ({ findings }: { findings: SiteIntelligenceVlanFinding[] }) => <ul className="site-intelligence-finding-list">
  {findings.map((finding) => <li key={finding.findingId}><p>{finding.text}</p><Confidence value={finding.confidence} /></li>)}
</ul>;

const VlanEvidence = ({ data }: { data: SiteIntelligenceVlanDetailResponse }) => {
  const membersByDevice = vlanMemberDeviceGroups(data);
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>Observed purpose</span>
      {data.purposes.length ? data.purposes.map((purpose) => <div key={purpose.findingId} className="site-intelligence-interpretation-card__finding"><p>{purpose.text}</p><Confidence value={purpose.confidence} /></div>) : <p>No purpose interpretation is available for this observed VLAN.</p>}
    </section>
    <dl className="site-intelligence-vlan-evidence-stats">
      <div><dt>Name status</dt><dd><span className={`site-intelligence-name-status site-intelligence-name-status--${data.vlan.nameStatus}`}>{data.vlan.nameStatus}</span></dd></div>
      <div><dt>Observed names</dt><dd>{data.vlan.nameObservations.length}</dd></div>
      <div><dt>Member devices</dt><dd>{data.vlan.memberDeviceCount}</dd></div>
      <div><dt>Member interfaces</dt><dd>{data.vlan.members.length}</dd></div>
    </dl>
    {data.commonalities.length ? <EvidenceGroup title="Common observed patterns"><VlanFindingList findings={data.commonalities} /></EvidenceGroup> : null}
    {data.observedDifferences.length ? <EvidenceGroup title="Observed naming differences"><VlanFindingList findings={data.observedDifferences} /></EvidenceGroup> : null}
    {data.reviewQuestions.length ? <EvidenceGroup title="Review questions"><VlanFindingList findings={data.reviewQuestions} /></EvidenceGroup> : null}
    <EvidenceGroup title="Observed names">
      {data.vlan.nameObservations.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Observed VLAN names and the member devices that reported them</caption>
        <thead><tr><th scope="col">Observed name</th><th scope="col">Reporting devices</th><th scope="col">Device count</th></tr></thead>
        <tbody>{data.vlan.nameObservations.map((observation) => <tr key={observation.name}><th scope="row">{observation.name}</th><td><DeviceChipList devices={data.devices} deviceIds={observation.deviceIds} /></td><td>{deviceIds(observation.deviceIds).length}</td></tr>)}</tbody>
      </table></div> : <p>No member device reported a VLAN name.</p>}
    </EvidenceGroup>
    <EvidenceGroup title="Observed membership">
      {membersByDevice.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-vlan-membership-table">
        <caption className="sr-only">Observed VLAN interface membership grouped by device</caption>
        <thead><tr><th scope="col">Device</th><th scope="col">Observed interfaces</th><th scope="col">Count</th></tr></thead>
        <tbody>{membersByDevice.map((member) => <tr key={member.deviceId}><th scope="row"><DeviceLink devices={data.devices} deviceId={member.deviceId} /></th><td><InterfaceChipList interfaceNames={member.interfaceNames} /></td><td>{member.interfaceNames.length}</td></tr>)}</tbody>
      </table></div> : <p>No interface memberships were observed for this VLAN.</p>}
    </EvidenceGroup>
  </div>;
};

export default SiteIntelligencePage;
